import { Note, NoteRelevance } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, AlertCircle, Star } from 'lucide-react';

const relevanceOptions: { value: NoteRelevance; label: string; icon: typeof FileText }[] = [
  { value: 'normal', label: 'Normal', icon: FileText },
  { value: 'importante', label: 'Importante', icon: AlertCircle },
  { value: 'destacada', label: 'Destacada', icon: Star },
];

interface NoteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note: Partial<Note> | null;
  onNoteChange: (note: Partial<Note>) => void;
  onSave: () => void;
}

export function NoteFormDialog({ open, onOpenChange, note, onNoteChange, onSave }: NoteFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{note?.id ? 'Editar Nota' : 'Crear Nota'}</DialogTitle>
          <DialogDescription className="sr-only">
            {note?.id ? 'Edita los campos de la nota' : 'Introduce los datos de la nueva nota'}
          </DialogDescription>
        </DialogHeader>
        {note && (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Título *</label>
              <Input
                placeholder="Título de la nota"
                value={note.title || ''}
                onChange={(e) => onNoteChange({ ...note, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Contenido *</label>
              <Textarea
                placeholder="Escribe tu nota aquí..."
                value={note.content || ''}
                onChange={(e) => onNoteChange({ ...note, content: e.target.value })}
                rows={10}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Relevancia</label>
              <Select
                value={note.relevance || 'normal'}
                onValueChange={(value: NoteRelevance) => onNoteChange({ ...note, relevance: value })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Relevancia" />
                </SelectTrigger>
                <SelectContent>
                  {relevanceOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span className="capitalize">{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {note.id && (
              <p className="text-xs text-muted-foreground">
                Para adjuntar archivos, guarda la nota y luego usa el botón "Adjuntar archivo" en la vista de la nota.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSave}>Guardar Nota</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
